import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { Alert, Button as MuiButton, Chip, MenuItem, Pagination, TextField } from '@mui/material'
import { ReactNode, useState } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom-v5-compat'
import {
  ComplianceWarning,
  DriverMetricsDetail,
  getDriverMetricDetail,
  MetricPeriod,
  OnTimeMetric,
} from 'shared/api/driverMetrics'
import { Button } from 'shared/components/Button/Button'
import { TextV2 } from 'shared/components/Text/TextV2'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'

export const number = (value: number) => value.toLocaleString('en-US', { maximumFractionDigits: 1 })
export const currency = (value: string | null) =>
  value === null
    ? '—'
    : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
const date = (value: string) => new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' })

export function PeriodSelect({
  value,
  onChange,
}: {
  value: MetricPeriod
  onChange: (value: MetricPeriod) => void
}) {
  return (
    <TextField
      select
      label="Reporting period"
      value={value}
      onChange={(event) => onChange(event.target.value as MetricPeriod)}
      size="small"
    >
      <MenuItem value="last_30">Last 30 days</MenuItem>
      <MenuItem value="lifetime">All recorded history</MenuItem>
    </TextField>
  )
}

export function WarningList({
  warnings,
  onboardingBase,
}: {
  warnings: ComplianceWarning[]
  onboardingBase?: string
}) {
  return (
    <div className="space-y-3">
      {warnings.length === 0 && <TextV2>No document warnings in the records checked.</TextV2>}
      {warnings.map((warning, index) => (
        <div
          key={`${warning.label}:${index}`}
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-solid border-amber-200 bg-amber-50 p-4"
        >
          <div>
            <TextV2 type={TYPOGRAPHY.label2}>{warning.label}</TextV2>
            <TextV2 type={TYPOGRAPHY.paragraph2} className="mt-1">
              {warning.status === 'unknown'
                ? 'Expiration date not recorded'
                : warning.status === 'missing'
                  ? 'Document not on file'
                  : `${warning.status === 'expired' ? 'Expired' : 'Expires'} ${date(warning.expires_on!)}`}
            </TextV2>
          </div>
          {warning.onboarding_id && onboardingBase && (
            <Link to={`${onboardingBase}${warning.onboarding_id}/`} className="text-royal-blue underline">
              Review document
            </Link>
          )}
        </div>
      ))}
    </div>
  )
}

function MetricCard({ title, value, children }: { title: string; value: string; children: ReactNode }) {
  return (
    <section className="min-w-0 rounded-xl border border-solid border-gray-200 bg-white p-5">
      <TextV2 type={TYPOGRAPHY.label2} className="text-mid-gray">
        {title}
      </TextV2>
      <TextV2 type={TYPOGRAPHY.header1} className="my-3 break-words">
        {value}
      </TextV2>
      <div className="text-mid-gray space-y-1">{children}</div>
    </section>
  )
}
function Change({
  current,
  previous,
  unit = '',
}: {
  current: number | null
  previous: number | null
  unit?: string
}) {
  if (current === null || previous === null)
    return <TextV2 type={TYPOGRAPHY.paragraph2}>Prior-period comparison unavailable</TextV2>
  const delta = current - previous
  return (
    <TextV2 type={TYPOGRAPHY.paragraph2}>
      {delta > 0 ? '+' : ''}
      {number(delta)}
      {unit} vs previous 30 days
    </TextV2>
  )
}
function OnTimeCard({
  title,
  current,
  previous,
  lifetime,
  compare,
}: {
  title: string
  current: OnTimeMetric
  previous: OnTimeMetric
  lifetime: OnTimeMetric
  compare: boolean
}) {
  return (
    <MetricCard title={title} value={current.percent === null ? '—' : `${current.percent}%`}>
      <TextV2 type={TYPOGRAPHY.paragraph2}>
        {current.on_time} of {current.measured} measured stops · {current.unknown} unknown
      </TextV2>
      {compare && <Change current={current.percent} previous={previous.percent} unit=" pp" />}
      <TextV2 type={TYPOGRAPHY.paragraph2}>
        Lifetime:{' '}
        {lifetime.percent === null
          ? 'No measured stops'
          : `${lifetime.percent}% (${lifetime.measured} stops)`}
      </TextV2>
    </MetricCard>
  )
}

function Trend({ data, money = false }: { data: DriverMetricsDetail['weekly']; money?: boolean }) {
  const values = data.map((item) => (money ? (item.gmv === null ? null : Number(item.gmv)) : item.jobs))
  const max = Math.max(1, ...values.map((value) => Math.abs(value || 0)))
  return (
    <section className="min-w-0 rounded-xl border border-solid border-gray-200 bg-white p-5">
      <TextV2 type={TYPOGRAPHY.header3}>{money ? 'Recorded gross revenue' : 'Completed jobs'}</TextV2>
      <TextV2 type={TYPOGRAPHY.paragraph2} className="text-mid-gray mt-1">
        Last 8 weeks · current week is partial · UTC{money && ' · USD'}
      </TextV2>
      <div
        className="mt-6 flex h-48 items-end gap-2"
        role="img"
        aria-label={`${money ? 'Gross revenue' : 'Completed jobs'} by week: ${data.map((item, i) => `${date(item.week)}: ${values[i] === null ? 'unavailable' : values[i]}`).join(', ')}`}
      >
        {data.map((item, i) => (
          <div key={item.week} className="flex h-full min-w-0 flex-1 flex-col justify-end gap-2 text-center">
            <TextV2
              type={TYPOGRAPHY.paragraph3}
              className="text-mid-gray truncate"
              title={money ? currency(item.gmv) : number(item.jobs)}
            >
              {values[i] === null
                ? '—'
                : money
                  ? new Intl.NumberFormat('en-US', {
                      notation: 'compact',
                      maximumFractionDigits: 1,
                    }).format(values[i]!)
                  : values[i]}
            </TextV2>
            <div
              className={`min-h-[2px] rounded-t-md ${(values[i] || 0) < 0 ? 'bg-amber-600' : 'bg-blue-500'}`}
              style={{ height: `${(72 * Math.abs(values[i] || 0)) / max}%` }}
            />
            <TextV2 type={TYPOGRAPHY.paragraph2} className="text-mid-gray whitespace-nowrap">
              {new Date(item.week).toLocaleDateString('en-US', {
                month: 'numeric',
                day: 'numeric',
                timeZone: 'UTC',
              })}
            </TextV2>
          </div>
        ))}
      </div>
    </section>
  )
}

export function PerformanceContent({
  data,
  period,
  onboardingBase,
  jobHref,
}: {
  data: DriverMetricsDetail
  period: MetricPeriod
  onboardingBase?: string
  jobHref?: (id: string) => string
}) {
  const current = data.metrics[period]
  const { lifetime, previous_30: previous } = data.metrics
  const compare = period === 'last_30'
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-1">
        <MetricCard title="Jobs completed" value={number(current.jobs)}>
          {compare && <Change current={current.jobs} previous={previous.jobs} />}
          <TextV2 type={TYPOGRAPHY.paragraph2}>Lifetime: {number(lifetime.jobs)}</TextV2>
        </MetricCard>
        <MetricCard title="Gross revenue" value={currency(current.gmv)}>
          {compare && (
            <Change
              current={current.gmv === null ? null : Number(current.gmv)}
              previous={previous.gmv === null ? null : Number(previous.gmv)}
              unit=" USD"
            />
          )}
          <TextV2 type={TYPOGRAPHY.paragraph2}>Lifetime: {currency(lifetime.gmv)}</TextV2>
          <TextV2 type={TYPOGRAPHY.paragraph2}>
            {current.gmv === null
              ? 'Not available for this period or your permissions.'
              : `${current.revenue_jobs} of ${current.jobs} jobs have recorded revenue. Before fees and deductions.`}
          </TextV2>
        </MetricCard>
        <OnTimeCard
          title="On-time delivery"
          current={current.delivery}
          previous={previous.delivery}
          lifetime={lifetime.delivery}
          compare={compare}
        />
        <OnTimeCard
          title="On-time pickup"
          current={current.pickup}
          previous={previous.pickup}
          lifetime={lifetime.pickup}
          compare={compare}
        />
      </div>
      <div className="grid grid-cols-3 gap-4 sm:grid-cols-1">
        <MetricCard title="Average revenue / job" value={currency(current.average_gmv)}>
          <TextV2 type={TYPOGRAPHY.paragraph2}>Available when every completed job has revenue.</TextV2>
        </MetricCard>
        <MetricCard title="Planned loaded miles" value={number(current.planned_miles)}>
          <TextV2 type={TYPOGRAPHY.paragraph2}>
            {current.mileage_jobs} of {current.jobs} jobs have mileage.
          </TextV2>
        </MetricCard>
        <MetricCard title="Revenue / planned mile" value={currency(current.revenue_per_mile)}>
          <TextV2 type={TYPOGRAPHY.paragraph2}>Uses complete revenue and planned mileage coverage.</TextV2>
        </MetricCard>
      </div>
      <TextV2 type={TYPOGRAPHY.paragraph2} className="text-mid-gray">
        {data.active_jobs} current jobs · Last completed job:{' '}
        {data.last_completed_at ? date(data.last_completed_at) : 'None recorded'}
      </TextV2>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-1">
        <Trend data={data.weekly} />
        <Trend data={data.weekly} money />
      </div>
      <section className="space-y-4 rounded-xl border border-solid border-gray-200 bg-white p-5">
        <TextV2 type={TYPOGRAPHY.header2}>Safety & compliance</TextV2>
        <TextV2 type={TYPOGRAPHY.paragraph2}>
          Current CDL, medical card and {data.personal ? 'your' : 'active onboarding'} document dates.
          Expiration warnings cover the next 30 days.
        </TextV2>
        <WarningList warnings={data.warnings} onboardingBase={onboardingBase} />
        <div className="grid grid-cols-3 gap-4 sm:grid-cols-1">
          {data.safety_history.map((category) => (
            <details
              key={category.label}
              className="min-w-0 rounded-lg border border-solid border-gray-200 p-4"
            >
              <summary className="cursor-pointer">
                <TextV2 type={TYPOGRAPHY.label2}>{category.label}</TextV2>
              </summary>
              <TextV2 type={TYPOGRAPHY.header2} className="my-3">
                {category.available ? category[period] : '—'}
              </TextV2>
              <TextV2 type={TYPOGRAPHY.paragraph2}>{category.source}</TextV2>
              <TextV2 type={TYPOGRAPHY.paragraph2} className="text-mid-gray mt-2">
                {category.updated_at ? `Record updated ${date(category.updated_at)}` : 'No records available'}{' '}
                · {category.unknown_dates} undated
              </TextV2>
              {category.events.map((event) => (
                <TextV2 key={event.id} type={TYPOGRAPHY.paragraph2} className="mt-2">
                  {event.date ? date(event.date) : 'Date unknown'} · {event.label}
                </TextV2>
              ))}
              <TextV2 type={TYPOGRAPHY.paragraph2} className="text-mid-gray mt-2">
                Latest 10 records across all dates. Reported history can include prior employment and is not a
                live safety score.
              </TextV2>
            </details>
          ))}
        </div>
        <Alert severity="info">
          Live driving events and hours-of-service violations are unavailable in this view until a verified
          carrier data feed is connected. Missing data does not indicate a clean safety record.
        </Alert>
      </section>
      <section className="space-y-4">
        <TextV2 type={TYPOGRAPHY.header2}>Jobs behind these metrics</TextV2>
        {!data.jobs.length && <TextV2>No completed jobs in this period.</TextV2>}
        {data.jobs.map((job) => (
          <details key={job.id} className="rounded-xl border border-solid border-gray-200 bg-white p-4">
            <summary className="cursor-pointer">
              Job {job.human_id} · completed {date(job.completed_at)}
            </summary>
            <div className="mt-3 space-y-3">
              {jobHref && (
                <Link to={jobHref(job.id)} className="text-royal-blue underline">
                  Open job
                </Link>
              )}
              {!job.stops.length && <TextV2>No stop records available.</TextV2>}
              {job.stops.map((stop, i) => {
                const known =
                  stop.actual_arrival &&
                  stop.date_end &&
                  stop.is_appointment_confirmed &&
                  (!stop.date_start ||
                    new Date(stop.date_start).getTime() <= new Date(stop.date_end).getTime())
                return (
                  <div key={i} className="flex flex-wrap items-center gap-3">
                    <Chip size="small" label={stop.type_detail} />
                    <TextV2 type={TYPOGRAPHY.paragraph2}>
                      {!known
                        ? 'Unknown'
                        : new Date(stop.actual_arrival!).getTime() <= new Date(stop.date_end!).getTime()
                          ? 'On time'
                          : 'Late'}{' '}
                      · Arrival:{' '}
                      {stop.actual_arrival ? new Date(stop.actual_arrival).toLocaleString() : 'Not recorded'}{' '}
                      · Appointment end:{' '}
                      {stop.date_end ? new Date(stop.date_end).toLocaleString() : 'Not recorded'}
                    </TextV2>
                  </div>
                )
              })}
            </div>
          </details>
        ))}
      </section>
      <details className="text-mid-gray text-sm">
        <summary className="cursor-pointer">How these metrics work</summary>
        <p>
          Completed jobs are delivered, non-canceled jobs with a recorded delivery timestamp. Periods use that
          timestamp and rolling 30-day windows. All-time includes only this company’s recorded history,
          attributed to the job’s assigned driver.
        </p>
        <p>
          Gross revenue sums non-void statement subtotals. It is not driver pay or profit. Missing revenue or
          mileage is not estimated. Revenue can change when statements are corrected.
        </p>
        <p>
          On-time means the earliest recorded arrival is at or before the confirmed appointment window’s end;
          early arrivals count. Each stop counts once across tracking sources. Estimates and missing or
          unconfirmed appointment times are excluded and shown as unknown. Pickup includes prehaul stops.
          Appointment corrections can change these figures; there are no automatic excused delays.
        </p>
        <p>
          Updated {new Date(data.updated_at).toLocaleString()}. Compliance warnings are current, independent
          of the reporting period.
        </p>
      </details>
    </div>
  )
}

export function DriverPerformance({
  id,
  actorKey,
  backHref,
  onboardingBase,
  jobHref,
}: {
  id: string
  actorKey: string
  backHref?: string
  onboardingBase?: string
  jobHref?: (id: string) => string
}) {
  const [period, setPeriod] = useState<MetricPeriod>('last_30')
  const [page, setPage] = useState(1)
  const query = useQuery(
    ['driver-metrics', actorKey, id, period, page],
    () => getDriverMetricDetail(id, period, page),
    { retry: false }
  )
  return (
    <main className="mx-auto max-w-6xl space-y-6">
      {backHref && (
        <MuiButton component={Link} to={backHref} startIcon={<ArrowBackIcon />}>
          All drivers
        </MuiButton>
      )}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <TextV2 type={TYPOGRAPHY.header1}>
            Driver performance <TrendingUpIcon />
          </TextV2>
          {query.data && !query.isError && (
            <TextV2 type={TYPOGRAPHY.paragraph1} className="text-mid-gray mt-2">
              {query.data.name} · {query.data.team.name}
            </TextV2>
          )}
        </div>
        <PeriodSelect
          value={period}
          onChange={(value) => {
            setPeriod(value)
            setPage(1)
          }}
        />
      </header>
      {query.isLoading ? (
        <TextV2 role="status">Loading performance…</TextV2>
      ) : query.isError || !query.data ? (
        <Alert severity="error" action={<Button onClick={() => query.refetch()}>Retry</Button>}>
          Unable to load driver performance. Access may be disabled by your company.{' '}
          {page > 1 && <button onClick={() => setPage(1)}>Return to first page</button>}
        </Alert>
      ) : (
        <>
          <PerformanceContent
            data={query.data}
            period={period}
            onboardingBase={onboardingBase}
            jobHref={jobHref}
          />
          {query.data.job_count > 10 && (
            <Pagination
              page={page}
              count={Math.ceil(query.data.job_count / 10)}
              onChange={(_, value) => setPage(value)}
              aria-label="Completed job pages"
            />
          )}
        </>
      )}
    </main>
  )
}
